// Combat Log UI - WoW-style scrolling combat text

export interface CombatLogEntry {
  timestamp: number;
  message: string;
  type: 'damage_dealt' | 'damage_taken' | 'heal' | 'buff' | 'debuff' | 'experience' | 'loot' | 'system';
  color: string;
}

export class CombatLog {
  private container: HTMLDivElement;
  private logContent: HTMLDivElement;
  private entries: CombatLogEntry[] = [];
  private maxEntries: number = 100;
  private isVisible: boolean = true;
  private isMinimized: boolean = false;

  // Color mapping for different message types
  private typeColors: Record<string, string> = {
    damage_dealt: '#ffff00',
    damage_taken: '#ff4444',
    heal: '#44ff44',
    buff: '#9999ff',
    debuff: '#ff6666',
    experience: '#cc99ff',
    loot: '#1eff00',
    system: '#888888',
  };

  constructor() {
    this.container = this.createContainer();
    document.getElementById('game-container')?.appendChild(this.container);

    // Toggle visibility with 'L' key
    window.addEventListener('keydown', (e) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.code === 'KeyL') {
        this.toggleVisibility();
      }
    });
  }

  private createContainer(): HTMLDivElement {
    const container = document.createElement('div');
    container.id = 'combat-log';
    container.style.cssText = `
      position: absolute;
      bottom: 100px;
      left: 15px;
      width: 320px;
      height: 150px;
      background: rgba(0, 0, 0, 0.6);
      border: 1px solid #4a4a6a;
      border-radius: 6px;
      font-family: 'Segoe UI', Arial, sans-serif;
      font-size: 12px;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      z-index: 90;
      pointer-events: auto;
    `;

    // Header
    const header = document.createElement('div');
    header.style.cssText = `
      padding: 5px 10px;
      background: rgba(0, 0, 0, 0.4);
      border-bottom: 1px solid #4a4a6a;
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
    `;
    header.innerHTML = `
      <span style="color: #ffd700; font-weight: bold; font-size: 11px;">Combat Log</span>
      <div>
        <button id="combat-log-minimize" style="
          background: none;
          border: none;
          color: #888;
          cursor: pointer;
          font-size: 14px;
          padding: 0 5px;
        ">─</button>
        <button id="combat-log-clear" style="
          background: none;
          border: none;
          color: #888;
          cursor: pointer;
          font-size: 10px;
          padding: 0 5px;
        ">Clear</button>
      </div>
    `;
    container.appendChild(header);

    // Log content area
    this.logContent = document.createElement('div');
    this.logContent.id = 'combat-log-content';
    this.logContent.style.cssText = `
      flex: 1;
      overflow-y: auto;
      padding: 5px 10px;
      scrollbar-width: thin;
      scrollbar-color: #4a4a6a transparent;
    `;
    container.appendChild(this.logContent);

    // Event listeners
    header.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).id === 'combat-log-minimize') {
        this.toggleMinimize();
      } else if ((e.target as HTMLElement).id === 'combat-log-clear') {
        this.clear();
      }
    });

    return container;
  }

  public log(message: string, type: CombatLogEntry['type'] = 'system'): void {
    const entry: CombatLogEntry = {
      timestamp: Date.now(),
      message,
      type,
      color: this.typeColors[type] || '#ffffff',
    };

    this.entries.push(entry);

    // Trim old entries
    if (this.entries.length > this.maxEntries) {
      this.entries.shift();
    }

    this.addEntryToDOM(entry);
  }

  private addEntryToDOM(entry: CombatLogEntry): void {
    const line = document.createElement('div');
    line.style.cssText = `
      color: ${entry.color};
      margin-bottom: 2px;
      line-height: 1.3;
      word-wrap: break-word;
    `;

    const time = new Date(entry.timestamp);
    const timeStr = `${time.getHours().toString().padStart(2, '0')}:${time.getMinutes().toString().padStart(2, '0')}:${time.getSeconds().toString().padStart(2, '0')}`;

    line.innerHTML = `<span style="color: #666; font-size: 10px;">[${timeStr}]</span> ${entry.message}`;

    this.logContent.appendChild(line);

    // Auto-scroll to bottom
    this.logContent.scrollTop = this.logContent.scrollHeight;
  }

  public logDamageDealt(targetName: string, damage: number, isCrit: boolean = false, skillName?: string): void {
    const critText = isCrit ? ' (Critical!)' : '';
    const skillText = skillName ? ` with ${skillName}` : '';
    this.log(`You hit ${targetName}${skillText} for ${damage} damage${critText}`, 'damage_dealt');
  }

  public logDamageTaken(sourceName: string, damage: number, isCrit: boolean = false): void {
    const critText = isCrit ? ' (Critical!)' : '';
    this.log(`${sourceName} hits you for ${damage} damage${critText}`, 'damage_taken');
  }

  public logHeal(amount: number, sourceName?: string): void {
    if (sourceName) {
      this.log(`${sourceName} heals you for ${amount}`, 'heal');
    } else {
      this.log(`You are healed for ${amount}`, 'heal');
    }
  }

  public logBuff(buffName: string, applied: boolean = true): void {
    if (applied) {
      this.log(`You gain ${buffName}`, 'buff');
    } else {
      this.log(`${buffName} fades`, 'buff');
    }
  }

  public logDebuff(debuffName: string, applied: boolean = true): void {
    if (applied) {
      this.log(`You are afflicted by ${debuffName}`, 'debuff');
    } else {
      this.log(`${debuffName} fades`, 'debuff');
    }
  }

  public logExperience(amount: number, sourceName?: string): void {
    if (sourceName) {
      this.log(`You gain ${amount} experience from ${sourceName}`, 'experience');
    } else {
      this.log(`You gain ${amount} experience`, 'experience');
    }
  }

  public logLoot(itemName: string, quantity: number = 1): void {
    if (quantity > 1) {
      this.log(`You receive loot: ${itemName} x${quantity}`, 'loot');
    } else {
      this.log(`You receive loot: ${itemName}`, 'loot');
    }
  }

  public logKill(targetName: string): void {
    this.log(`${targetName} dies`, 'system');
  }

  public clear(): void {
    this.entries = [];
    this.logContent.innerHTML = '';
  }

  private toggleVisibility(): void {
    this.isVisible = !this.isVisible;
    this.container.style.display = this.isVisible ? 'flex' : 'none';
  }

  private toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;
    this.logContent.style.display = this.isMinimized ? 'none' : 'block';
    this.container.style.height = this.isMinimized ? 'auto' : '150px';
  }

  public destroy(): void {
    this.container.remove();
  }
}
